// ============================================
// Contact Detail Page
// ============================================

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from '@tanstack/react-router';
import { AppLayout } from '@/components/AppLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { DetailPageSkeleton } from '@/components/skeletons';
import { type UpdateContact } from '@muraka/shared';
import {
  ArrowLeft,
  Pencil,
  Trash2,
  Mail,
  Phone,
  Smartphone,
  Building2,
  Star,
  Loader2,
  AlertCircle,
  MoreVertical,
} from 'lucide-react';

// Avatar colors for consistent coloring based on name
const avatarColors = [
  'bg-blue-100 text-blue-700',
  'bg-green-100 text-green-700',
  'bg-amber-100 text-amber-700',
  'bg-purple-100 text-purple-700',
  'bg-red-100 text-red-700',
  'bg-pink-100 text-pink-700',
];

// Icon colors for account card
const iconColors = [
  'bg-blue-100 text-blue-600',
  'bg-green-100 text-green-600',
  'bg-amber-100 text-amber-600',
  'bg-purple-100 text-purple-600',
  'bg-red-100 text-red-600',
  'bg-pink-100 text-pink-600',
];

// Get initials from name
const getInitials = (firstName: string, lastName: string) => {
  return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase() || 'U';
};

// Get avatar color based on name
const getAvatarColor = (name: string) => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return avatarColors[Math.abs(hash) % avatarColors.length];
};

// Get icon color based on name
const getIconColor = (name: string) => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return iconColors[Math.abs(hash) % iconColors.length];
};

// Format date for display
const formatFullDate = (date: string) => {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export function ContactDetailPage() {
  const { id } = useParams({ from: '/contacts/$id' });
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // State
  const [activeTab, setActiveTab] = useState('details');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    mobile: '',
    title: '',
    department: '',
    accountId: '',
    isPrimary: false,
    notes: '',
  });

  // Query contact
  const {
    data: contact,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['contact', id],
    queryFn: () => api.getContact(id),
    enabled: !!id,
  });

  // Query accounts for dropdown
  const { data: accountsData } = useQuery({
    queryKey: ['accounts-list'],
    queryFn: () => api.getAccounts({ pageSize: 100 }),
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: UpdateContact) => api.updateContact(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contact', id] });
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      setEditModalOpen(false);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: () => api.deleteContact(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      navigate({ to: '/contacts' });
    },
  });

  // Set primary mutation
  const setPrimaryMutation = useMutation({
    mutationFn: () => api.setContactPrimary(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contact', id] });
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      queryClient.invalidateQueries({ queryKey: ['account-contacts'] });
    },
  });

  // Open edit modal with current data
  const openEditModal = () => {
    if (contact) {
      setFormData({
        firstName: contact.firstName,
        lastName: contact.lastName,
        email: contact.email || '',
        phone: contact.phone || '',
        mobile: contact.mobile || '',
        title: contact.title || '',
        department: contact.department || '',
        accountId: contact.accountId,
        isPrimary: contact.isPrimary || false,
        notes: contact.notes || '',
      });
      setEditModalOpen(true);
    }
  };

  const handleUpdate = (e: React.FormEvent) => {
    e.preventDefault();
    const updateData: UpdateContact = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email || null,
      phone: formData.phone || null,
      mobile: formData.mobile || null,
      title: formData.title || null,
      department: formData.department || null,
      isPrimary: formData.isPrimary,
      notes: formData.notes || null,
    };
    updateMutation.mutate(updateData);
  };

  const accounts = accountsData?.data || [];

  if (isLoading) {
    return (
      <AppLayout>
        <div className="p-4 md:p-6 max-w-7xl mx-auto w-full">
          <DetailPageSkeleton />
        </div>
      </AppLayout>
    );
  }

  if (error || !contact) {
    return (
      <AppLayout>
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <AlertCircle className="h-12 w-12 text-destructive mb-4" />
          <p className="text-destructive mb-2">Failed to load contact</p>
          <Button variant="outline" onClick={() => navigate({ to: '/contacts' })}>
            Back to Contacts
          </Button>
        </div>
      </AppLayout>
    );
  }

  const fullName = `${contact.firstName} ${contact.lastName}`;

  return (
    <AppLayout>
      {/* Contact Header Section */}
      <div className="bg-card border-b border-border -mx-4 lg:-mx-6 -mt-6 mb-6">
        {/* Back Button Bar */}
        <div className="h-14 border-b border-border flex items-center px-4 lg:px-6">
          <button
            onClick={() => navigate({ to: '/contacts' })}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Back to Contacts</span>
          </button>
        </div>

        {/* Contact Info Header */}
        <div className="max-w-5xl mx-auto px-4 lg:px-6 py-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            {/* Contact Info */}
            <div className="flex items-start gap-4">
              <div
                className={`w-16 h-16 rounded-full flex items-center justify-center text-xl font-semibold shrink-0 ${getAvatarColor(fullName)}`}
              >
                {getInitials(contact.firstName, contact.lastName)}
              </div>
              <div>
                <div className="flex items-center gap-3 flex-wrap">
                  <h1 className="text-2xl font-bold text-foreground">{fullName}</h1>
                  {contact.isPrimary && (
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                      Primary Contact
                    </span>
                  )}
                </div>
                <p className="text-muted-foreground mt-1">
                  {contact.title || 'No title'}
                  {contact.accountName && (
                    <>
                      {' at '}
                      <button
                        onClick={() =>
                          navigate({ to: '/accounts/$id', params: { id: contact.accountId } })
                        }
                        className="font-medium text-primary hover:underline"
                      >
                        {contact.accountName}
                      </button>
                    </>
                  )}
                </p>
                <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-muted-foreground">
                  {contact.email && (
                    <span className="flex items-center gap-1.5">
                      <Mail className="h-4 w-4" />
                      <a href={`mailto:${contact.email}`} className="hover:text-primary">
                        {contact.email}
                      </a>
                    </span>
                  )}
                  {contact.phone && (
                    <span className="flex items-center gap-1.5">
                      <Phone className="h-4 w-4" />
                      <a href={`tel:${contact.phone}`} className="hover:text-primary">
                        {contact.phone}
                      </a>
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={openEditModal}>
                <Pencil className="h-4 w-4" />
                Edit
              </Button>
              <button
                onClick={() => setPrimaryMutation.mutate()}
                disabled={setPrimaryMutation.isPending}
                className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  contact.isPrimary
                    ? 'text-amber-700 bg-amber-100 hover:bg-amber-200'
                    : 'text-foreground bg-background border border-border hover:bg-muted'
                }`}
              >
                {setPrimaryMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Star className="h-4 w-4" />
                )}
                {contact.isPrimary ? 'Primary' : 'Set Primary'}
              </button>
              <div className="relative">
                <button
                  onClick={() => setMoreMenuOpen(!moreMenuOpen)}
                  className="p-2 text-muted-foreground hover:text-foreground rounded-lg hover:bg-muted"
                >
                  <MoreVertical className="h-5 w-5" />
                </button>
                {moreMenuOpen && (
                  <div className="absolute right-0 top-full mt-1 w-48 bg-card rounded-lg shadow-lg border border-border py-1 z-10">
                    <button
                      onClick={() => {
                        setDeleteModalOpen(true);
                        setMoreMenuOpen(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-destructive hover:bg-destructive/10 flex items-center gap-2"
                    >
                      <Trash2 className="h-4 w-4" />
                      Delete Contact
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-card border-b border-border -mx-4 lg:-mx-6 mb-6 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 lg:px-6">
          <nav className="flex gap-6 -mb-px">
            <button
              onClick={() => setActiveTab('details')}
              className={`py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'details'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              Details
            </button>
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-5xl mx-auto">
        {/* Details Tab */}
        {activeTab === 'details' && (
          <div className="grid gap-6 lg:grid-cols-3">
            {/* Main Content (2 columns) */}
            <div className="lg:col-span-2 space-y-6">
              {/* Contact Information Card */}
              <div className="bg-card rounded-xl border border-border">
                <div className="px-6 py-4 border-b border-border flex items-center justify-between">
                  <h2 className="font-semibold text-foreground">Contact Information</h2>
                  <button onClick={openEditModal} className="text-sm text-primary hover:underline">
                    Edit
                  </button>
                </div>
                <div className="p-6">
                  <dl className="grid gap-6 sm:grid-cols-2">
                    <div>
                      <dt className="text-sm text-muted-foreground">Full Name</dt>
                      <dd className="mt-1 text-foreground">{fullName || '—'}</dd>
                    </div>
                    <div>
                      <dt className="text-sm text-muted-foreground">Job Title</dt>
                      <dd className="mt-1 text-foreground">{contact.title || '—'}</dd>
                    </div>
                    <div>
                      <dt className="text-sm text-muted-foreground">Email</dt>
                      <dd className="mt-1">
                        {contact.email ? (
                          <a
                            href={`mailto:${contact.email}`}
                            className="text-primary hover:underline"
                          >
                            {contact.email}
                          </a>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm text-muted-foreground">Phone</dt>
                      <dd className="mt-1">
                        {contact.phone ? (
                          <a href={`tel:${contact.phone}`} className="text-primary hover:underline">
                            {contact.phone}
                          </a>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm text-muted-foreground">Mobile</dt>
                      <dd className="mt-1">
                        {contact.mobile ? (
                          <a
                            href={`tel:${contact.mobile}`}
                            className="text-primary hover:underline"
                          >
                            {contact.mobile}
                          </a>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm text-muted-foreground">Department</dt>
                      <dd className="mt-1 text-foreground">{contact.department || '—'}</dd>
                    </div>
                  </dl>
                </div>
              </div>

              {/* Account Card */}
              <div className="bg-card rounded-xl border border-border">
                <div className="px-6 py-4 border-b border-border">
                  <h2 className="font-semibold text-foreground">Account</h2>
                </div>
                <div className="p-6">
                  {contact.accountName ? (
                    <button
                      onClick={() =>
                        navigate({ to: '/accounts/$id', params: { id: contact.accountId } })
                      }
                      className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg hover:bg-muted transition-colors w-full text-left"
                    >
                      <div
                        className={`w-12 h-12 rounded-lg flex items-center justify-center ${getIconColor(contact.accountName || '')}`}
                      >
                        <Building2 className="h-6 w-6" />
                      </div>
                      <div>
                        <p className="font-medium text-foreground">{contact.accountName}</p>
                        <p className="text-sm text-muted-foreground">View account details</p>
                      </div>
                    </button>
                  ) : (
                    <p className="text-muted-foreground italic">No account associated.</p>
                  )}
                </div>
              </div>

              {/* Notes Card */}
              <div className="bg-card rounded-xl border border-border">
                <div className="px-6 py-4 border-b border-border flex items-center justify-between">
                  <h2 className="font-semibold text-foreground">Notes</h2>
                  <button onClick={openEditModal} className="text-sm text-primary hover:underline">
                    Edit
                  </button>
                </div>
                <div className="p-6">
                  {contact.notes ? (
                    <p className="text-foreground whitespace-pre-wrap">{contact.notes}</p>
                  ) : (
                    <p className="text-muted-foreground italic">No notes added yet.</p>
                  )}
                </div>
              </div>
            </div>

            {/* Sidebar (1 column) */}
            <div className="space-y-6">
              {/* Quick Actions Card */}
              <div className="bg-card rounded-xl border border-border">
                <div className="px-6 py-4 border-b border-border">
                  <h2 className="font-semibold text-foreground">Quick Actions</h2>
                </div>
                <div className="p-6 space-y-3">
                  {contact.email && (
                    <a
                      href={`mailto:${contact.email}`}
                      className="flex items-center gap-3 p-3 rounded-lg border border-border hover:border-primary/30 hover:bg-muted/50 transition-colors"
                    >
                      <div className="w-8 h-8 rounded bg-blue-100 flex items-center justify-center">
                        <Mail className="h-4 w-4 text-blue-600" />
                      </div>
                      <span className="text-sm font-medium text-foreground">Send Email</span>
                    </a>
                  )}
                  {contact.phone && (
                    <a
                      href={`tel:${contact.phone}`}
                      className="flex items-center gap-3 p-3 rounded-lg border border-border hover:border-primary/30 hover:bg-muted/50 transition-colors"
                    >
                      <div className="w-8 h-8 rounded bg-green-100 flex items-center justify-center">
                        <Phone className="h-4 w-4 text-green-600" />
                      </div>
                      <span className="text-sm font-medium text-foreground">Call</span>
                    </a>
                  )}
                  {contact.mobile && (
                    <a
                      href={`tel:${contact.mobile}`}
                      className="flex items-center gap-3 p-3 rounded-lg border border-border hover:border-primary/30 hover:bg-muted/50 transition-colors"
                    >
                      <div className="w-8 h-8 rounded bg-purple-100 flex items-center justify-center">
                        <Smartphone className="h-4 w-4 text-purple-600" />
                      </div>
                      <span className="text-sm font-medium text-foreground">Call Mobile</span>
                    </a>
                  )}
                </div>
              </div>

              {/* System Information Card */}
              <div className="bg-card rounded-xl border border-border">
                <div className="px-6 py-4 border-b border-border">
                  <h2 className="font-semibold text-foreground">System Information</h2>
                </div>
                <div className="p-6 space-y-4">
                  <div>
                    <dt className="text-sm text-muted-foreground">Created</dt>
                    <dd className="mt-1 text-foreground">{formatFullDate(contact.createdAt)}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-muted-foreground">Last Modified</dt>
                    <dd className="mt-1 text-foreground">{formatFullDate(contact.updatedAt)}</dd>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      <Dialog open={editModalOpen} onClose={() => setEditModalOpen(false)}>
        <DialogContent onClose={() => setEditModalOpen(false)} className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Contact</DialogTitle>
            <DialogDescription>Update contact information</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdate}>
            <div className="p-6 pt-0 space-y-6 max-h-[calc(100vh-200px)] overflow-y-auto">
              {/* Contact Information Section */}
              <div>
                <h3 className="text-sm font-medium text-foreground mb-4 flex items-center gap-2">
                  <svg
                    className="w-4 h-4 text-muted-foreground"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                  Contact Information
                </h3>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Input
                    label="First Name"
                    required
                    placeholder="John"
                    value={formData.firstName}
                    onChange={e => setFormData({ ...formData, firstName: e.target.value })}
                  />
                  <Input
                    label="Last Name"
                    required
                    placeholder="Smith"
                    value={formData.lastName}
                    onChange={e => setFormData({ ...formData, lastName: e.target.value })}
                  />
                  <Input
                    label="Email"
                    type="email"
                    placeholder="john@company.com"
                    value={formData.email}
                    onChange={e => setFormData({ ...formData, email: e.target.value })}
                  />
                  <Input
                    label="Phone"
                    placeholder="+1 (555) 123-4567"
                    value={formData.phone}
                    onChange={e => setFormData({ ...formData, phone: e.target.value })}
                  />
                  <Input
                    label="Mobile"
                    placeholder="+1 (555) 987-6543"
                    value={formData.mobile}
                    onChange={e => setFormData({ ...formData, mobile: e.target.value })}
                  />
                </div>
              </div>

              {/* Work Information Section */}
              <div>
                <h3 className="text-sm font-medium text-foreground mb-4 flex items-center gap-2">
                  <svg
                    className="w-4 h-4 text-muted-foreground"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                  Work Information
                </h3>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Select
                    label="Account"
                    value={formData.accountId}
                    onChange={e => setFormData({ ...formData, accountId: e.target.value })}
                  >
                    <option value="">Select account</option>
                    {accounts.map((account: { id: string; name: string }) => (
                      <option key={account.id} value={account.id}>
                        {account.name}
                      </option>
                    ))}
                  </Select>
                  <Input
                    label="Job Title"
                    placeholder="CEO, VP of Sales, etc."
                    value={formData.title}
                    onChange={e => setFormData({ ...formData, title: e.target.value })}
                  />
                  <Input
                    label="Department"
                    placeholder="Sales, Marketing, etc."
                    value={formData.department}
                    onChange={e => setFormData({ ...formData, department: e.target.value })}
                  />
                </div>
              </div>

              {/* Options Section */}
              <div>
                <h3 className="text-sm font-medium text-foreground mb-4 flex items-center gap-2">
                  <svg
                    className="w-4 h-4 text-muted-foreground"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  Options
                </h3>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.isPrimary}
                    onChange={e => setFormData({ ...formData, isPrimary: e.target.checked })}
                    className="w-4 h-4 rounded border-input text-primary focus:ring-primary"
                  />
                  <span className="text-sm text-foreground">
                    Set as primary contact for this account
                  </span>
                </label>
                <p className="text-xs text-muted-foreground mt-2">
                  Primary contacts are the main point of contact for an account.
                </p>
              </div>

              {/* Notes Section */}
              <div>
                <h3 className="text-sm font-medium text-foreground mb-4 flex items-center gap-2">
                  <svg
                    className="w-4 h-4 text-muted-foreground"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                  Notes
                </h3>
                <Textarea
                  rows={3}
                  placeholder="Additional notes about this contact..."
                  value={formData.notes}
                  onChange={e => setFormData({ ...formData, notes: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter className="bg-muted/30">
              <Button type="button" variant="outline" onClick={() => setEditModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={updateMutation.isPending}>
                {updateMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Modal */}
      <Dialog open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
        <DialogContent onClose={() => setDeleteModalOpen(false)} className="max-w-md">
          <div className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center">
                <AlertCircle className="h-5 w-5 text-destructive" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">Delete Contact?</h3>
                <p className="text-sm text-muted-foreground">This action cannot be undone.</p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mb-6">
              Are you sure you want to delete{' '}
              <span className="font-medium text-foreground">{fullName}</span>? All associated data
              will be permanently removed.
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Delete Contact
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
